import React from "react";
import { useNavigate } from "react-router-dom";
import { PiggyBank, Receipt, FileText } from "lucide-react";
import { fmtMoney, SectionCard, SectionError } from "./dashUtils";

const Row = ({ label, value, cls = "text-foreground", testid }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-border/40 last:border-0" data-testid={testid}>
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className={`text-sm font-semibold ${cls}`}>{value}</span>
  </div>
);

export const FinancialSnapshot = ({ financial, onRetry }) => {
  if (!financial) return null;
  return (
    <SectionCard title="Financial Overview" icon={PiggyBank} testid="financial-snapshot">
      {financial.error ? (
        <SectionError label="financial data" onRetry={onRetry} />
      ) : (
        <div>
          <Row label="Invoice Sales (issued)" value={fmtMoney(financial.invoice_sales)} testid="fin-sales" />
          <Row label="Money Collected" value={fmtMoney(financial.collected)} cls="text-green-600" testid="fin-collected" />
          <Row label="Revenue Before Tax" value={fmtMoney(financial.revenue_before_tax)} testid="fin-revenue" />
          <Row label="Tax Collected" value={fmtMoney(financial.tax_collected)} cls="text-teal-600" testid="fin-tax" />
          <Row label="Deposits Held (open jobs)" value={fmtMoney(financial.deposits_held)} cls="text-cyan-600" testid="fin-deposits" />
          <Row label="Outstanding Balance" value={fmtMoney(financial.outstanding)} cls="text-orange-500" testid="fin-outstanding" />
          <Row label="Technician Commissions" value={fmtMoney(financial.tech_commissions)} cls="text-purple-500" testid="fin-commissions" />
          <Row label="Parts Cost" value={fmtMoney(financial.parts_cost)} cls="text-rose-500" testid="fin-parts" />
          <Row label="Processing Fees (from techs)" value={fmtMoney(financial.processing_fees)} cls="text-emerald-500" testid="fin-processing-fees" />
          <Row label="Net Profit" value={fmtMoney(financial.net_profit)} cls={financial.net_profit >= 0 ? "text-emerald-600" : "text-red-500"} testid="fin-profit" />
          <p className="text-[10px] text-muted-foreground mt-2">
            Commissions and profit are pre-tax — tax and credit fees are never commissioned.
          </p>
        </div>
      )}
    </SectionCard>
  );
};

export const DocumentsSnapshot = ({ documents, outstanding, financialAccess }) => {
  const navigate = useNavigate();
  if (!documents || documents.error) return null;
  const est = documents.estimates || {};
  const chip = (label, value, cls, to, testid) => (
    <button onClick={() => navigate(to)} className={`rounded-lg border border-border px-2.5 py-2 text-left hover:border-blue-500/40 transition-colors`} data-testid={testid}>
      <p className={`text-base font-bold font-['Manrope'] ${cls}`}>{value}</p>
      <p className="text-[10px] text-muted-foreground leading-tight">{label}</p>
    </button>
  );
  return (
    <SectionCard title="Invoices & Estimates" icon={Receipt} testid="documents-snapshot">
      <div className="grid grid-cols-3 gap-2">
        {chip("Unpaid / Partial", outstanding?.count ?? 0, "text-orange-500", "/dashboard/invoices", "doc-unpaid")}
        {chip("Overdue", outstanding?.overdue_count ?? 0, "text-red-500", "/dashboard/invoices", "doc-overdue")}
        {chip("Paid (period)", documents.paid_in_period ?? 0, "text-emerald-600", "/dashboard/invoices", "doc-paid")}
        {chip("Drafts", documents.drafts ?? 0, "text-muted-foreground", "/dashboard/invoices", "doc-drafts")}
        {chip("Estimates", est.created ?? 0, "text-blue-500", "/dashboard/invoices", "doc-estimates")}
        {chip("Est. Accepted", est.accepted ?? 0, "text-emerald-600", "/dashboard/invoices", "doc-est-accepted")}
      </div>
      {financialAccess && (
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/50">
          <span className="text-[11px] text-muted-foreground flex items-center gap-1"><FileText className="w-3 h-3" /> Estimate value (period)</span>
          <span className="text-sm font-semibold text-foreground">{fmtMoney(est.value)}</span>
        </div>
      )}
    </SectionCard>
  );
};
